import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FaCalendarAlt,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaQuoteLeft,
  FaSearch,
  FaTwitter,
} from "react-icons/fa";
import { fetchBlogDetail, fetchBlogsPage } from "../api/blogsPage";
import { useHomePage } from "../context/HomePageContext";

const SHARE_LINKS = [
  { label: "Facebook", Icon: FaFacebookF, href: "https://www.facebook.com/" },
  { label: "Instagram", Icon: FaInstagram, href: "https://www.instagram.com/" },
  { label: "LinkedIn", Icon: FaLinkedinIn, href: "https://www.linkedin.com/" },
  { label: "Twitter", Icon: FaTwitter, href: "https://twitter.com/" },
];

const BlogDetailPage = () => {
  const { id } = useParams();
  const { setContact } = useHomePage();
  const [blog, setBlog] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadBlogDetail = async () => {
      try {
        const [detail, list] = await Promise.all([
          fetchBlogDetail(id),
          fetchBlogsPage(),
        ]);
        if (!isMounted) return;

        setBlog(detail.blog);
        setRecentPosts(
          (list.blogs || []).filter((item) => String(item.id) !== String(id)).slice(0, 3)
        );
        if (detail.contact) {
          setContact(detail.contact);
        }
      } catch (err) {
        console.error(err);
        if (isMounted) setBlog(null);
      }
    };

    loadBlogDetail();

    return () => {
      isMounted = false;
    };
  }, [id, setContact]);

  if (!blog) return null;

  const query = search.trim().toLowerCase();
  const sidebarPosts = query
    ? recentPosts.filter((item) => item.title.toLowerCase().includes(query))
    : recentPosts;

  const tagSet = new Set();
  if (blog.tag) tagSet.add(blog.tag);
  if (blog.highlight) {
    blog.highlight.split(/\s+/).filter(Boolean).forEach((part) => tagSet.add(part));
  }
  recentPosts.forEach((post) => {
    if (post.tag) tagSet.add(post.tag);
    if (post.highlight) {
      post.highlight.split(/\s+/).filter(Boolean).forEach((part) => tagSet.add(part));
    }
  });
  const tagItems = [...tagSet];
  const highlightWords = (blog.highlight || "").split(/\s+/).filter(Boolean);

  return (
    <section className="bg-white pb-16 pt-10 md:pb-20 md:pt-12 lg:pb-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1.7fr)_minmax(280px,0.7fr)] lg:gap-12">
          <article>
            <div className="overflow-hidden rounded-[14px] bg-[#F7F7F7]">
              <img
                src={blog.image}
                alt={blog.title}
                referrerPolicy="no-referrer"
                className="aspect-[16/9] h-auto w-full object-cover"
              />
            </div>

            <div className="mt-5 flex items-center gap-2 text-brand-orange">
              <FaCalendarAlt className="text-[14px]" aria-hidden="true" />
              <time className="text-[14px] font-medium">{blog.date}</time>
            </div>

            <h1 className="mt-3 text-[28px] font-bold leading-tight text-brand-purple md:text-[34px]">
              {blog.title}
            </h1>

            {blog.description ? (
              <p className="mt-5 text-[15px] leading-[1.85] text-[#555555] md:text-[16px]">
                {blog.description}
              </p>
            ) : null}

            {blog.description ? (
              <blockquote className="relative mt-7 overflow-hidden rounded-[12px] bg-brand-orange px-6 py-7 text-white md:px-8 md:py-8">
                <FaQuoteLeft
                  className="absolute left-5 top-5 text-[28px] text-white/35 md:left-6 md:top-6"
                  aria-hidden="true"
                />
                <p className="relative z-[1] pl-8 text-[15px] font-medium leading-[1.75] md:pl-10 md:text-[16px]">
                  {blog.description}
                </p>
              </blockquote>
            ) : null}

            <div className="mt-8 flex flex-col gap-5 border-t border-[#E8E8E8] pt-6 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[14px] text-[#666666]">
                Tags:{" "}
                {highlightWords.length > 0 ? (
                  highlightWords.map((word, index) => (
                    <span key={word} className="font-semibold text-brand-purple">
                      {word}
                      {index < highlightWords.length - 1 ? " " : ""}
                    </span>
                  ))
                ) : blog.tag ? (
                  <span className="font-semibold text-brand-purple">{blog.tag}</span>
                ) : (
                  <span className="font-semibold text-brand-purple">General</span>
                )}
              </p>

              <div className="flex items-center gap-3">
                <span className="text-[14px] text-[#666666]">Share:</span>
                <div className="flex items-center gap-2">
                  {SHARE_LINKS.map(({ label, Icon, href }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={label}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-purple text-white transition-opacity hover:opacity-85"
                    >
                      <Icon className="text-[12px]" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </article>

          <aside className="space-y-8">
            <form
              onSubmit={(e) => e.preventDefault()}
              className="flex overflow-hidden rounded-[8px] border border-[#E4E4E4] bg-white"
            >
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Enter your keywords..."
                className="w-full px-4 py-3 text-[14px] text-[#444444] outline-none"
              />
              <button
                type="submit"
                className="flex h-auto w-12 shrink-0 items-center justify-center bg-brand-orange text-white"
                aria-label="Search posts"
              >
                <FaSearch className="text-[14px]" />
              </button>
            </form>

            <div>
              <h2 className="text-[20px] font-bold text-brand-purple">Recent Posts</h2>
              <div className="mt-5 space-y-5">
                {sidebarPosts.length > 0 ? (
                  sidebarPosts.map((post) => (
                    <Link
                      key={post.id}
                      to={post.to}
                      className="flex gap-3 transition-opacity hover:opacity-85"
                    >
                      <img
                        src={post.image}
                        alt=""
                        aria-hidden="true"
                        referrerPolicy="no-referrer"
                        className="h-[72px] w-[72px] shrink-0 rounded-[8px] object-cover"
                      />
                      <div className="min-w-0">
                        <h3 className="text-[14px] font-bold leading-snug text-brand-purple">
                          {post.title}
                        </h3>
                        <div className="mt-2 flex items-center gap-1.5 text-brand-orange">
                          <FaCalendarAlt className="text-[11px]" aria-hidden="true" />
                          <time className="text-[12px] font-medium">{post.date}</time>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="text-[14px] text-[#777777]">No recent posts found.</p>
                )}
              </div>
            </div>

            {tagItems.length > 0 ? (
              <div>
                <h2 className="text-[20px] font-bold text-brand-purple">Tags</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {tagItems.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-[6px] bg-brand-purple px-3 py-1.5 text-[12px] font-medium text-white"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </aside>
        </div>
      </div>
    </section>
  );
};

export default BlogDetailPage;
